import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { fetchAllData } from '../lib/api';
import { Song as SongType, Artist, Voicebank, CopyrightInfo } from '../types/models';
import { usePlayer } from '../lib/PlayerContext';
import { useAuth } from '../context/AuthContext';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import LyricsIcon from '@mui/icons-material/Lyrics';
import { getArtistNames, getPrimaryArtist } from '../utils/artistUtils';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CopyrightIcon from '@mui/icons-material/Copyright';
import { storage } from '../lib/api';
import { Link } from 'react-router-dom';
import { CopyrightInfoModal } from '../components/CopyrightInfoModal';

export function Song() {
  const { id } = useParams<{ id: string }>();
  const [song, setSong] = useState<SongType | null>(null);
  const [artist, setArtist] = useState<Artist | null>(null);
  const [allArtists, setAllArtists] = useState<Artist[]>([]);
  const [voicebank, setVoicebank] = useState<Voicebank | null>(null);
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  const [showCopyrightModal, setShowCopyrightModal] = useState(false);
  const player = usePlayer();
  const { user } = useAuth();

  // Check if a song is in favorites
  const checkFavoriteStatus = async (songId: string) => {
    if (!user) {
      // Use local storage for guests
      const favorites = storage.getFavorites();
      setIsFavorite(favorites.includes(songId));
      return;
    }
    
    try {
      // Use API for authenticated users
      const token = localStorage.getItem('auth_token');
      if (!token) return;
      
      const apiBase = import.meta.env.VITE_API_BASE || '/api';
      const response = await fetch(`${apiBase}/favorites?songId=${songId}`, {
        method: 'HEAD',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      setIsFavorite(response.ok);
    } catch (error) {
      console.error('Error checking favorite status:', error);
    }
  };

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        const data = await fetchAllData();
        
        if (id) {
          const foundSong = data.songs.find(s => s.id === id);
          if (foundSong) {
            setSong(foundSong);
            setAllArtists(data.artists);
            
            // Set primary artist for backward compatibility with existing UI
            const foundArtist = getPrimaryArtist(foundSong.artistId, data.artists);
            if (foundArtist) setArtist(foundArtist);
            
            const foundVoicebank = data.voicebanks.find(v => v.id === foundSong.synthId);
            if (foundVoicebank) setVoicebank(foundVoicebank);
            
            // Check if song is favorited
            await checkFavoriteStatus(foundSong.id);
          }
        }
      } catch (error) {
        console.error('Error loading song data:', error);
      } finally {
        setLoading(false);
      }
    }
    
    loadData();
  }, [id, user]); // Re-run when user auth state changes

  const toggleFavorite = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!song) return;
    setFavoriteLoading(true);
    
    try {
      if (user) {
        // Use API for authenticated users
        const token = localStorage.getItem('auth_token');
        if (!token) return;
        
        const apiBase = import.meta.env.VITE_API_BASE || '/api';
        
        if (isFavorite) {
          // Remove from favorites
          await fetch(`${apiBase}/favorites?songId=${song.id}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
        } else {
          // Add to favorites
          await fetch(`${apiBase}/favorites`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ songId: song.id })
          });
        }
      } else {
        // Use local storage for guests
        const favorites = storage.getFavorites();
        const newFavorites = isFavorite
          ? favorites.filter(favId => favId !== song.id)
          : [...favorites, song.id];
        
        storage.setFavorites(newFavorites);
      }
      
      // Toggle the UI state
      setIsFavorite(!isFavorite);
    } catch (error) {
      console.error('Error toggling favorite:', error);
    } finally {
      setFavoriteLoading(false);
    }
  };

  const playSong = () => {
    if (song) {
      player.playSong(song);
    }
  };

  const getCopyrightInfo = (): CopyrightInfo => {
    if (!song) {
      return {
        freeForUse: false,
        attributionRequired: false,
        doNotMonetize: false,
        doNotUse: false
      };
    }

    return {
      freeForUse: song.copyrightFreeForUse || false,
      attributionRequired: song.copyrightAttributionRequired || false,
      doNotMonetize: song.copyrightDoNotMonetize || false,
      doNotUse: song.copyrightDoNotUse || false,
      customText: song.copyrightCustomText
    };
  };

  const hasCopyrightInfo = () => {
    const info = getCopyrightInfo();
    return info.freeForUse || info.attributionRequired || info.doNotMonetize || info.doNotUse || info.customText;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-naia-pink text-xl">Loading...</div>
      </div>
    );
  }

  if (!song || !artist) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-slate-grey text-xl">Song not found</div>
      </div>
    );
  }

  return (
    <div className="pt-24 pb-32 px-6 max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row gap-8 items-start">
        {/* Song Cover */}
        <div className="w-full md:w-1/3 lg:w-1/4">
          <div className="relative aspect-square rounded-xl overflow-hidden shadow-2xl shadow-naia-pink/10 ring-1 ring-white/10 group">
            <img 
              src={song.coverUrl} 
              alt={song.title}
              className="w-full h-full object-cover"
            />
            {!song.isOriginal && (
              <span className="absolute top-3 left-3 px-2 py-1 bg-naia-pink rounded-full text-xs font-medium text-pearl-white shadow-lg shadow-naia-pink/20 backdrop-blur-sm">
                Cover
              </span>
            )}
            <div className="absolute inset-0 flex items-center justify-center bg-deep-night/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <button 
                onClick={playSong}
                className="p-4 bg-naia-pink rounded-full transform scale-90 group-hover:scale-100 transition-transform duration-300"
              >
                <PlayArrowIcon style={{ fontSize: '2.5rem' }} className="text-pearl-white" />
              </button>
            </div>
          </div>
        </div>

        {/* Song Info */}
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-3xl md:text-4xl font-bold text-pearl-white">{song.title}</h1>
            <button
              onClick={toggleFavorite}
              disabled={favoriteLoading}
              className={`p-2 hover:bg-white/10 rounded-full transition-all duration-300 hover:scale-110 ${favoriteLoading ? 'opacity-50' : ''}`}
            >
              {favoriteLoading ? (
                <div className="w-6 h-6 border-2 border-naia-pink border-t-transparent rounded-full animate-spin"></div>
              ) : isFavorite ? (
                <FavoriteIcon className="text-naia-pink drop-shadow-glow" />
              ) : (
                <FavoriteBorderIcon className="text-slate-grey" />
              )}
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-3 mt-2 mb-6">
            <Link 
              to={`/artist/${artist.id}`}
              className="flex items-center gap-2 px-3 py-1.5 bg-white/5 hover:bg-white/10 rounded-full transition-all duration-300"
            >
              <img 
                src={artist.imageUrl} 
                alt={artist.name}
                className="w-6 h-6 rounded-full object-cover"
              />
              <span className="text-slate-grey hover:text-pearl-white transition-colors">
                {getArtistNames(song.artistId, allArtists)}
              </span>
            </Link>

            {voicebank && (
              <Link 
                to={`/voicebank/${voicebank.id}`}
                className="px-3 py-1.5 bg-white/5 hover:bg-white/10 rounded-full text-slate-grey hover:text-pearl-white transition-all duration-300"
              >
                {voicebank.name}
              </Link>
            )}

            <div className="px-3 py-1.5 bg-white/5 rounded-full text-slate-grey">
              {Math.floor(song.duration / 60)}:{(song.duration % 60).toString().padStart(2, '0')}
            </div>

            {/* Categories removed as requested */}
          </div>

          <div className="flex flex-wrap gap-3 mt-4">
            <button 
              onClick={playSong}
              className="px-6 py-3 bg-naia-pink hover:bg-naia-pink/90 rounded-full text-pearl-white font-medium flex items-center gap-2 transition-all duration-300 shadow-lg shadow-naia-pink/20"
            >
              <PlayArrowIcon />
              Play
            </button>

            {song.buyUrl && song.buyUrl !== "x" && (
              <a
                href={song.buyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-3 bg-white/5 hover:bg-white/10 rounded-full text-slate-grey hover:text-pearl-white font-medium flex items-center gap-2 transition-all duration-300"
              >
                <ShoppingCartIcon />
                Purchase
              </a>
            )}

            {song.lrcUrl && song.lrcUrl !== "x" && (
              <a
                href={song.lrcUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-3 bg-white/5 hover:bg-white/10 rounded-full text-slate-grey hover:text-pearl-white font-medium flex items-center gap-2 transition-all duration-300"
              >
                <LyricsIcon />
                Lyrics
              </a>
            )}

            {hasCopyrightInfo() && (
              <button
                onClick={() => setShowCopyrightModal(true)}
                className="px-6 py-3 bg-white/5 hover:bg-white/10 rounded-full text-slate-grey hover:text-pearl-white font-medium flex items-center gap-2 transition-all duration-300"
              >
                <CopyrightIcon />
                Copyright Info
              </button>
            )}
          </div>

          {/* Copyright and Record Label Info */}
          {(song.copyright || song.recordLabel) && (
            <div className="mt-8 text-sm text-slate-grey">
              {song.copyright && (
                <div className="mb-1">
                  <span className="font-medium">Copyright:</span> {song.copyright}
                </div>
              )}
              {song.recordLabel && (
                <div>
                  <span className="font-medium">Record Label:</span> {song.recordLabel}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Copyright Info Modal */}
      {hasCopyrightInfo() && (
        <CopyrightInfoModal
          isOpen={showCopyrightModal}
          onClose={() => setShowCopyrightModal(false)}
          copyrightInfo={getCopyrightInfo()}
          songTitle={song?.title || ''}
          artistName={artist?.name || ''}
        />
      )}
    </div>
  );
}
